import React from "react";
import _str from "shared/util/_str";

export const STEPS = {
	ESSAY: "Essay",
	QUESTIONS: "Questions",
	QUESTIONS_CONVO: "QuestionsConvo",
	PHOTOS: "PhotoOrganizer",
	BASICS: "Basics",
	PROFILE_DETAILS: "ProfileDetails",
	EDUCATION_OCCUPATION: "EducationOccupation",
	WHAT_I_WANT: "WhatIWant",
	PASSIONS_PICKER: "PassionsPicker",
	PASSIONS_ESSAY: "PassionsEssay",
	PHONE: "Phone",
};

export const ESSAY_LENGTH_CUTOFFS = {
	OK: 16,
	GOOD: 31,
};

export const USER_THUMB_SIZE = "225x225";

export const QUESTION_CARD_CLASS = "obquestions-cards-card";
export const QUESTION_CARD_COUNT = 3;
export const QUESTION_CARD_IMPORTANCE = "SOMEWHAT";
export const QUESTION_CARD_TRANSITION_TIME = 700;
export const QUESTION_CARD_VOTE_THRESHOLD = 80;

export const QCONVO_ANSWER_DELAY = 0;
export const QCONVO_QUESTION_SPEED = 500;
export const QCONVO_SUBMIT_DELAY = 600;

export const MATCH_THUMBNAIL_SIZE = 400;
export const MATCH_THUMBNAIL_SIZE_INDEX = `${MATCH_THUMBNAIL_SIZE}x${MATCH_THUMBNAIL_SIZE}`;
export const MATCH_FETCH_LIMIT = 18;
export const MATCH_REQUEST_FIELDS = [
	"userinfo{ age, location }",
	`thumbs{ ${MATCH_THUMBNAIL_SIZE_INDEX} }.limit(1)`,
].join(",");
export const EXTRA_SCROLL_OFFSET = 200;
export const DOUBLE_TAP_TIME = 400;

export const REQUIREMENTS = {
	[STEPS.QUESTIONS]: {
		name: {
			singular: "question",
			plural: "questions",
		},
	},
};

export const STEP_INTRO = {
	[STEPS.WHAT_I_WANT]: {
		title: _str("Who are you looking for?"),
		subtitle: _str("To see the right people, tell us who you’re into."),
	},

	[STEPS.BASICS]: {
		title: _str("Let's start with the basics"),
		subtitle: _str("Set up your profile to meet new people."),
	},

	[STEPS.PHOTOS]: {
		title: _str("Tell us about yourself"),
		subtitle: _str("So we can find people who like you for you."),
	},

	[STEPS.PASSIONS_PICKER]: {
		title: _str("Personalize your connections"),
		subtitle: _str("Find people based on what matters to you!"),
	},

	[STEPS.PASSIONS_ESSAY]: {
		title: _str("Nicely done!"),
		subtitle: _str("Customize your connections by sharing more about your topics."),
	},
};

export const REBOARDING_STEP_INTRO = {
	[STEPS.WHAT_I_WANT]: {
		title: _str("OkCupid recommendations just got better!"),
		subtitle: _str("Update the type of relationships you’re interested in so we can recommend the best people for you."),
	},
	[STEPS.PASSIONS_PICKER]: {
		title: _str("Introducing Topics"),
		subtitle: _str("Personalize your matches based on what matters to you!"),
	},
	[STEPS.PROFILE_DETAILS]: {
		title: _str("Tell us about yourself"),
		subtitle: (
			<React.Fragment>
				<p>{_str("This will only take a minute!")}</p>
				<p>{_str("In order to show you to the right people, we need some details about you first.")}</p>
			</React.Fragment>
		),
	},
};

export const REQUIREMENT = {
	IDEAL_PERSON: "ideal person",
	BASICS: "basics",
	PROFILE_DETAILS: "profile details",
	LOCATION: "location",
	GENDER: "gender",
	DOB: "date of birth",
	USERNAME: "username",
	PASSWORD: "password",
	ESSAY: "essay",
	QUESTIONS: "questions",
};

// list each requirement field by their app / step in onboarding
export const REQUIREMENT_FIELD_MAP = {
	"ACR_LOOKING_FOR_RELATIONSHIP": REQUIREMENT.IDEAL_PERSON,
	"ACR_LOOKING_FOR_DISTANCE": REQUIREMENT.IDEAL_PERSON,
	"ACR_LOOKING_FOR_GENDER": REQUIREMENT.IDEAL_PERSON,
	"ACR_LOOKING_FOR_AGE": REQUIREMENT.IDEAL_PERSON,

	"ACR_HEIGHT": REQUIREMENT.PROFILE_DETAILS,
	"ACR_EDUCATION": REQUIREMENT.PROFILE_DETAILS,
	"ACR_OCCUPATION": REQUIREMENT.PROFILE_DETAILS,
	"ACR_RELIGION": REQUIREMENT.PROFILE_DETAILS,
	"ACR_RELIGION_SERIOUSNESS": REQUIREMENT.PROFILE_DETAILS,
	"ACR_DIET": REQUIREMENT.PROFILE_DETAILS,
	"ACR_DIET_SERIOUSNESS": REQUIREMENT.PROFILE_DETAILS,
	"ACR_FAMILY": REQUIREMENT.PROFILE_DETAILS,
	"ACR_PETS": REQUIREMENT.PROFILE_DETAILS,
	"ACR_SMOKING": REQUIREMENT.PROFILE_DETAILS,
	"ACR_DRINKING": REQUIREMENT.PROFILE_DETAILS,
	"ACR_DRUGS": REQUIREMENT.PROFILE_DETAILS,
	"ACR_DATE_OF_BIRTH": REQUIREMENT.BASICS,
	"ACR_ETHNICITY": REQUIREMENT.PROFILE_DETAILS,

	"ACR_GENDER": REQUIREMENT.GENDER,
	"ACR_LOCATION": REQUIREMENT.BASICS,

	"ACR_USERNAME": REQUIREMENT.USERNAME,
	"ACR_PASSWORD": REQUIREMENT.PASSWORD,

	"ACR_PHOTO": REQUIREMENT.PHOTO,

	"ACR_NO_ESSAY": REQUIREMENT.ESSAY,
	"ACR_ESSAY_TOO_SHORT": REQUIREMENT.ESSAY,

	"ACR_QUESTIONS": REQUIREMENT.QUESTIONS,
};

export const REQUIREMENT_STEP_MAP = {
	"ACR_PASSIONS_PICKER": STEPS.PASSIONS_PICKER,
	"ACR_PASSIONS_ESSAY": STEPS.PASSIONS_ESSAY,

	"ACR_LOOKING_FOR_RELATIONSHIP": STEPS.WHAT_I_WANT,
	"ACR_LOOKING_FOR_DISTANCE": STEPS.WHAT_I_WANT,
	"ACR_LOOKING_FOR_GENDER": STEPS.WHAT_I_WANT,
	"ACR_LOOKING_FOR_AGE": STEPS.WHAT_I_WANT,
	"ACR_ORIENTATION": STEPS.WHAT_I_WANT,

	"ACR_HEIGHT": STEPS.PROFILE_DETAILS,
	"ACR_EDUCATION": STEPS.PROFILE_DETAILS,
	"ACR_OCCUPATION": STEPS.PROFILE_DETAILS,
	"ACR_RELIGION": STEPS.PROFILE_DETAILS,
	"ACR_RELIGION_SERIOUSNESS": STEPS.PROFILE_DETAILS,
	"ACR_DIET": STEPS.PROFILE_DETAILS,
	"ACR_DIET_SERIOUSNESS": STEPS.PROFILE_DETAILS,
	"ACR_FAMILY": STEPS.PROFILE_DETAILS,
	"ACR_PETS": STEPS.PROFILE_DETAILS,
	"ACR_SMOKING": STEPS.PROFILE_DETAILS,
	"ACR_DRINKING": STEPS.PROFILE_DETAILS,
	"ACR_DRUGS": STEPS.PROFILE_DETAILS,
	"ACR_DATE_OF_BIRTH": STEPS.BASICS,
	"ACR_ETHNICITY": STEPS.PROFILE_DETAILS,
	"ACR_GENDER": STEPS.BASICS,
	"ACR_LOCATION": STEPS.BASICS,

	"ACR_USERNAME": STEPS.REGISTRATION,
	"ACR_PASSWORD": STEPS.REGISTRATION,

	"ACR_PHOTO": STEPS.PHOTOS,

	"ACR_NO_ESSAY": STEPS.ESSAY,
	"ACR_ESSAY_TOO_SHORT": STEPS.ESSAY,

	"ACR_QUESTIONS": STEPS.QUESTIONS_CONVO,
};



// WEBPACK FOOTER //
// ./src/apps/onboarding/util/constants.js